/* eslint-disable max-lines -- Governed media identities are maintained as one auditable registry. */
/**
 * Media Asset Registry
 *
 * Centralized registry for all build-hashed media assets (images, videos, animations, screenshots, posters, WebGL scenes).
 * Archetype descriptors reference asset KEYS only; routes never pass literal
 * URLs, fit styles, focal points, timings, or media renderer callbacks.
 * This constraint ensures consistent authorship, versioning, and DS governance.
 *
 * MANDATORY: Every media asset imported or used must be registered here.
 * DS components (FmAssetImage, FmMediaPanel, FmBackgroundMediaBand) consume keys only.
 *
 * POLICY — muem9.20:
 * - kind: 'image' | 'video' | 'animation' | 'map-poster' | 'screenshot' | 'poster'
 * - provenance: metadata for source (screenshotRoute, generatedPrompt, etc.)
 * - webglPolicy: when a WebGL surface is allowed + required fallback poster
 * - NO-FALLBACK: unregistered/failed assets render governed poster or nothing, never fabricated
 */

import { CONTEXT_MEDIA_REGISTRY } from './contextMediaRegistry';
import { PUBLISHED_MEDIA_REGISTRY } from './publishedMediaRegistry';
import forestSentryProductMedia from '@/assets/media/product-forest-sentry.png';
import measurementEvidenceProductMedia from '@/assets/media/product-measurement-evidence.png';
import measurementLiveFiMedia from '@/assets/media/product-measurements-live-fi.png';
import offerReviewProductMedia from '@/assets/media/product-offer-review.png';
import offerWorkflowLiveFiMedia from '@/assets/media/product-offer-workflow-live-fi.png';
import propertyOverviewProductMedia from '@/assets/media/product-property-overview.png';
import valuationLiveFiMedia from '@/assets/media/product-valuation-live-fi.png';
import rainEvidenceMapPoster from '@/assets/media/map-rain-evidence.svg';
import forestOverviewReportMedia from '@/assets/media/report-forest-overview.svg';
import planningDemoMedia from '@/assets/media/product-planning-demo.png';
import planningSuccessFiMedia from '@/assets/media/product-planning-success-fi.svg';
import planningExportMedia from '@/assets/media/product-planning-export.png';
import forestHeroAlternatives from '@/assets/media/product-forest-hero-alternatives.jpg';
import forestHeroIncome from '@/assets/media/product-forest-hero-income.jpg';
import stockForestBoardwalk from '@/assets/media/stock-forest-boardwalk.jpg';
import stockForestPineUnderstory from '@/assets/media/stock-forest-pine-understory.jpg';
import stockForestRoadPlan from '@/assets/media/stock-forest-road-plan.jpg';
import stockForestTrailEvening from '@/assets/media/stock-forest-trail-evening.jpg';
import measurementFieldPhoto from '@/assets/media/product-phone-forest-measurement.jpg';
import valuationFilmEn from '@/assets/media/lehti-valuation-en.mp4';
import valuationFilmFi from '@/assets/media/lehti-valuation-fi.mp4';
import valuationFilmEnMobile from '@/assets/media/lehti-valuation-en-mobile.mp4';
import valuationFilmFiMobile from '@/assets/media/lehti-valuation-fi-mobile.mp4';
import valuationPosterEn from '@/assets/media/lehti-valuation-poster-en.jpg';
import valuationPosterFi from '@/assets/media/lehti-valuation-poster-fi.jpg';
import valuationPosterEnMobile from '@/assets/media/lehti-valuation-poster-en-mobile.jpg';
import valuationPosterFiMobile from '@/assets/media/lehti-valuation-poster-fi-mobile.jpg';
import planJourneyEn from '@/assets/media/lehti-plan-journey-en.mp4';
import planJourneyFi from '@/assets/media/lehti-plan-journey-fi.mp4';
import planJourneyPosterEn from '@/assets/media/lehti-plan-journey-poster-en.jpg';
import planJourneyPosterFi from '@/assets/media/lehti-plan-journey-poster-fi.jpg';

/**
 * Provenance metadata for media assets.
 * Tracks source, capture/generation details, and replacement policy.
 */
export type FmMediaKind = 'image' | 'video' | 'animation' | 'map-poster' | 'screenshot' | 'poster';

export interface FmMediaProvenance {
  /** Source of media: 'skogiq-app' | 'skogiq-data-render' | 'generated' | 'licensed-stock' | 'manual-upload' */
  source: 'skogiq-app' | 'skogiq-data-render' | 'generated' | 'licensed-stock' | 'manual-upload';
  /** ISO timestamp of capture/generation */
  createdAt: string;
  /** For app screenshots: route path or identifier */
  screenshotRoute?: string;
  /** For app screenshots: seeded data identifier (e.g., property ID or demo seed) */
  screenshotSeed?: string;
  /** For generated imagery: the prompt used to generate */
  generatedPrompt?: string;
  /** For generated imagery: the model used (e.g., 'dall-e-3', 'midjourney') */
  generatedModel?: string;
  /** Data dependencies for time-based replacement (e.g., 'property:123', 'forecast:2026-07') */
  dataDependencies?: string[];
  /** For licensed/manual media: original creator/photographer name */
  creator?: string;
  /** For licensed/manual media: canonical source page URL */
  sourceUrl?: string;
  /** For licensed/manual media: canonical license URL */
  licenseUrl?: string;
  /** ISO timestamp when the source and license were last verified. */
  retrievedAt?: string;
  /** Human-readable license version/date captured during verification. */
  licenseVersion?: string;
  /** Replacement policy: 'stable' (permanent), 'regenerate-on-release', 'regenerate-on-data-change', 'temporary' */
  replacementPolicy?:
    | 'stable'
    | 'regenerate-on-release'
    | 'regenerate-on-data-change'
    | 'temporary';
}

/**
 * WebGL / heavy-interactive media policy.
 * Defines when a live WebGL surface is allowed and what fallback to use.
 */
export interface FmMediaWebGlPolicy {
  /** Whether WebGL/Canvas/live interactive is allowed for this asset */
  webglAllowed: boolean;
  /** Fallback static poster asset key if WebGL fails or is disabled */
  fallbackAssetKey?: string;
  /** Fallback for users with prefers-reduced-motion (static poster key) */
  reducedMotionFallback?: string;
}

/**
 * Media asset shape. FmMediaAsset defines all possible properties for media at the DS level.
 * Components consume subsets of this based on their purpose (e.g., FmAssetImage ignores animation props).
 */
export interface FmMediaAsset {
  /** Unique registry key, e.g., 'forest-overview-hero' or 'map.stand-detail.webgl' */
  key: string;
  /** Asset kind for component selection and validation */
  kind: FmMediaKind;
  /** Primary source URL (imported/hashed) */
  src: string;
  /** Optional srcSet for responsive images (media query breakpoints) */
  srcSet?: string;
  /** Optional poster frame for videos or WebGL fallback (key reference) */
  poster?: string;
  /** Registry key of the narrow-screen rendition (a portrait encode or crop) shown at phone widths. */
  mobileVariant?: string;
  /** Alt text for accessibility and fallback rendering */
  alt: string;
  /** Optional artist/source credit */
  credit?: string;
  /** Optional license info */
  license?: string;
  /** Aspect ratio hint for frame sizing and lazy rendering */
  aspect: 'wide' | 'square' | 'portrait' | 'panorama';
  /** Focal point for responsive cropping: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'X% Y%' */
  focalPoint?: 'center' | 'top' | 'bottom' | 'left' | 'right' | string;
  /** Dominant color tone for scrim/overlay matching (FmBackgroundMediaBand) */
  dominantTone?: 'light' | 'dark' | 'green' | 'warm' | 'cool';
  /** Auto-play behavior for video/animation */
  autoplay?: boolean;
  /** Loop behavior for video/animation */
  loop?: boolean;
  /** Whether this asset requires credit/attribution display */
  requiresCredit?: boolean;
  /** Provenance: source, capture details, replacement policy */
  provenance: FmMediaProvenance;
  /** WebGL policy: when live surface is allowed + fallback */
  webglPolicy: FmMediaWebGlPolicy;
  /** Runtime delivery metadata. Never exposed to route descriptors. */
  delivery?: {
    catalogVersion: string;
    objectKey: string;
    checksum: string;
  };
  /** Governed crop choices selected by canonical component placement. */
  cropProfiles?: Partial<
    Record<
      'background' | 'split' | 'card' | 'entity-card' | 'mobile',
      {
        aspect: FmMediaAsset['aspect'];
        focalPoint: string;
        note: string;
        /** Optional governed detail crop. Values above 1 enlarge the source inside its clipped frame. */
        scale?: number;
      }
    >
  >;
  /** Forestry-safe semantic labels approved for illustrative matching. */
  semanticContext?: {
    species?: 'pine' | 'spruce' | 'birch' | 'deciduous' | 'mixed';
    developmentClass?: 'A0' | 'T1' | 'T2';
    landClass?: 'nonproductive-peatland';
    structure?: 'pine-overstory-spruce-regeneration' | 'birch-overstory-spruce-regeneration';
    condition?: 'conifer-mortality' | 'broken-trees';
    illustrative: true;
  };
}

/**
 * Registry: a flat keyed map of all governed media assets.
 * Asset files live under src/assets/media so Vite produces build-hashed URLs.
 */
const CORE_MEDIA_REGISTRY = {
  'product.measurement-field-photo': {
    key: 'product.measurement-field-photo',
    kind: 'image',
    src: measurementFieldPhoto,
    alt: 'A person holding a phone toward trees in a forest',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'green',
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T18:00:00Z',
      sourceUrl: 'https://images.pexels.com/photos/5978370/pexels-photo-5978370.jpeg',
      licenseUrl: 'https://www.pexels.com/license/',
      retrievedAt: '2026-09-06T18:00:00Z',
      licenseVersion: 'Pexels License; attribution optional, verified 2026-09-06',
      dataDependencies: ['editorial-stock-photo:not-an-app-screenshot'],
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.valuation-film.en': {
    key: 'product.valuation-film.en',
    kind: 'video',
    src: valuationFilmEn,
    poster: 'product.valuation-film.poster-en',
    alt: 'Illustrated concept film explaining the inspectable basis of a Finnish forest-property valuation',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    autoplay: true,
    loop: true,
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:52:00Z',
      creator: 'Lehti AI',
      dataDependencies: ['summa-arvo-method', 'luke-price-history:2016-2025'],
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false, reducedMotionFallback: 'product.valuation-film.poster-en' },
  },
  'product.valuation-film.fi': {
    key: 'product.valuation-film.fi',
    kind: 'video',
    src: valuationFilmFi,
    poster: 'product.valuation-film.poster-fi',
    alt: 'Havainnollistava konseptivideo suomalaisen metsätilan hinta-arvion tarkistettavista perusteista',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    autoplay: true,
    loop: true,
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:52:00Z',
      creator: 'Lehti AI',
      dataDependencies: ['summa-arvo-method', 'luke-price-history:2016-2025'],
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false, reducedMotionFallback: 'product.valuation-film.poster-fi' },
  },
  'product.valuation-film.mobile-en': {
    key: 'product.valuation-film.mobile-en',
    kind: 'video',
    src: valuationFilmEnMobile,
    poster: 'product.valuation-film.poster-mobile-en',
    alt: 'Phone-layout concept film explaining the inspectable basis of a Finnish forest-property valuation',
    aspect: 'portrait',
    focalPoint: 'center',
    dominantTone: 'dark',
    autoplay: true,
    loop: true,
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:52:00Z',
      creator: 'Lehti AI',
      dataDependencies: ['summa-arvo-method', 'luke-price-history:2016-2025'],
      replacementPolicy: 'stable',
    },
    webglPolicy: {
      webglAllowed: false,
      reducedMotionFallback: 'product.valuation-film.poster-mobile-en',
    },
  },
  'product.valuation-film.mobile-fi': {
    key: 'product.valuation-film.mobile-fi',
    kind: 'video',
    src: valuationFilmFiMobile,
    poster: 'product.valuation-film.poster-mobile-fi',
    alt: 'Puhelimelle sommiteltu konseptivideo suomalaisen metsätilan hinta-arvion tarkistettavista perusteista',
    aspect: 'portrait',
    focalPoint: 'center',
    dominantTone: 'dark',
    autoplay: true,
    loop: true,
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:52:00Z',
      creator: 'Lehti AI',
      dataDependencies: ['summa-arvo-method', 'luke-price-history:2016-2025'],
      replacementPolicy: 'stable',
    },
    webglPolicy: {
      webglAllowed: false,
      reducedMotionFallback: 'product.valuation-film.poster-mobile-fi',
    },
  },
  'product.valuation-film.poster-en': {
    key: 'product.valuation-film.poster-en',
    kind: 'poster',
    src: valuationPosterEn,
    alt: 'Poster for the forest valuation basis concept film',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:52:00Z',
      creator: 'Lehti AI',
      dataDependencies: ['summa-arvo-method'],
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.valuation-film.poster-fi': {
    key: 'product.valuation-film.poster-fi',
    kind: 'poster',
    src: valuationPosterFi,
    alt: 'Metsätilan hinta-arvion perusteita kuvaavan konseptivideon aloituskuva',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:52:00Z',
      creator: 'Lehti AI',
      dataDependencies: ['summa-arvo-method'],
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.valuation-film.poster-mobile-en': {
    key: 'product.valuation-film.poster-mobile-en',
    kind: 'poster',
    src: valuationPosterEnMobile,
    alt: 'Phone-layout poster for the forest valuation basis concept film',
    aspect: 'portrait',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:52:00Z',
      creator: 'Lehti AI',
      dataDependencies: ['summa-arvo-method'],
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.valuation-film.poster-mobile-fi': {
    key: 'product.valuation-film.poster-mobile-fi',
    kind: 'poster',
    src: valuationPosterFiMobile,
    alt: 'Puhelimelle sommitellun hinta-arviovideon aloituskuva',
    aspect: 'portrait',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:52:00Z',
      creator: 'Lehti AI',
      dataDependencies: ['summa-arvo-method'],
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.plan-journey.en': {
    key: 'product.plan-journey.en',
    kind: 'video',
    src: planJourneyEn,
    poster: 'product.plan-journey.poster-en',
    alt: 'Illustrated Lehti AI concept film from forest information through planning and offer comparison',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    autoplay: true,
    loop: true,
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:32:36Z',
      creator: 'Lehti AI',
      sourceUrl:
        'https://2u7fo.upcloudobjects.com/lehti-media/catalog/v1/product.plan-journey/2026-09-06-3dee6adbd444/manifest.json',
      dataDependencies: ['concept-film:illustrative-data'],
      replacementPolicy: 'stable',
    },
    webglPolicy: {
      webglAllowed: false,
      reducedMotionFallback: 'product.plan-journey.poster-en',
    },
  },
  'product.plan-journey.fi': {
    key: 'product.plan-journey.fi',
    kind: 'video',
    src: planJourneyFi,
    poster: 'product.plan-journey.poster-fi',
    alt: 'Lehti AI:n havainnollistava konseptivideo metsätiedosta suunnitelmaan ja tarjousvertailuun',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    autoplay: true,
    loop: true,
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:32:36Z',
      creator: 'Lehti AI',
      sourceUrl:
        'https://2u7fo.upcloudobjects.com/lehti-media/catalog/v1/product.plan-journey/2026-09-06-3dee6adbd444/manifest.json',
      dataDependencies: ['concept-film:illustrative-data'],
      replacementPolicy: 'stable',
    },
    webglPolicy: {
      webglAllowed: false,
      reducedMotionFallback: 'product.plan-journey.poster-fi',
    },
  },
  'product.plan-journey.poster-en': {
    key: 'product.plan-journey.poster-en',
    kind: 'poster',
    src: planJourneyPosterEn,
    alt: 'Poster for the illustrated Lehti AI plan journey concept film',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:32:36Z',
      creator: 'Lehti AI',
      sourceUrl:
        'https://2u7fo.upcloudobjects.com/lehti-media/catalog/v1/product.plan-journey/2026-09-06-3dee6adbd444/manifest.json',
      dataDependencies: ['concept-film:illustrative-data'],
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.plan-journey.poster-fi': {
    key: 'product.plan-journey.poster-fi',
    kind: 'poster',
    src: planJourneyPosterFi,
    alt: 'Lehti AI:n havainnollistavan suunnitelmavideon aloituskuva',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: {
      source: 'manual-upload',
      createdAt: '2026-09-06T10:32:36Z',
      creator: 'Lehti AI',
      sourceUrl:
        'https://2u7fo.upcloudobjects.com/lehti-media/catalog/v1/product.plan-journey/2026-09-06-3dee6adbd444/manifest.json',
      dataDependencies: ['concept-film:illustrative-data'],
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.forest-sentry.hero': {
    key: 'product.forest-sentry.hero',
    kind: 'screenshot',
    src: forestSentryProductMedia,
    alt: 'Forest Sentry owner workspace showing the current monitoring access state',
    credit: 'Lehti AI product example · demonstration property data',
    aspect: 'panorama',
    focalPoint: 'center',
    dominantTone: 'green',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-07-14T00:00:00Z',
      screenshotRoute: '/app/watch?property=90-428-1-32',
      screenshotSeed: 'landing-real-route-90-428-1-32',
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: {
      webglAllowed: false,
    },
  },
  'product.forest-sentry.alert-fi': {
    key: 'product.forest-sentry.alert-fi',
    kind: 'poster',
    src: forestSentryProductMedia,
    alt: 'Havainnollistava Metsävahti-kuva näyttää, miten riski, sijainti ja seuraava toimenpide kootaan yhteen',
    aspect: 'square',
    focalPoint: 'center',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-data-render',
      createdAt: '2026-09-05T00:00:00Z',
      dataDependencies: ['illustration:forest-sentry-risk-explanation'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.forest-sentry.alert-mobile-fi': {
    key: 'product.forest-sentry.alert-mobile-fi',
    kind: 'poster',
    src: forestSentryProductMedia,
    alt: 'Havainnollistava mobiilirajaus Metsävahdin riskistä ja seuraavasta tarkastustoimenpiteestä',
    aspect: 'square',
    focalPoint: 'center',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-data-render',
      createdAt: '2026-09-05T00:00:00Z',
      dataDependencies: ['illustration:forest-sentry-risk-explanation'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.measurements.demo': {
    key: 'product.measurements.demo',
    kind: 'screenshot',
    src: measurementEvidenceProductMedia,
    alt: 'Measurement workspace showing measured coverage and the public-data baseline',
    credit: 'Lehti AI product example · demonstration property data',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-07-14T00:00:00Z',
      screenshotRoute: '/app/measurements/properties/90-428-1-32',
      screenshotSeed: 'landing-real-route-90-428-1-32',
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.measurements.live-fi': {
    key: 'product.measurements.live-fi',
    kind: 'screenshot',
    src: measurementLiveFiMedia,
    alt: 'Lehti AI:n suomenkielinen mittaustyötila näyttää aidon maastokartan, metsäkuviot sekä puhelinskannauksen arvot julkiseen metsävaratietoon verrattuna',
    aspect: 'wide',
    focalPoint: 'right',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-09-05T00:00:00Z',
      screenshotRoute: '/app/measurements/properties/178-415-5-2?stand=27388643',
      screenshotSeed: 'admin-property-178-415-5-2-stand-27388643',
      dataDependencies: [
        'property:178-415-5-2',
        'measurement-campaign:990',
        'stand:27388643',
        'mml:maastokartta',
      ],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      split: {
        aspect: 'wide',
        focalPoint: '72% 55%',
        note: 'Keep the selected forest and measured-versus-public evidence pane legible',
      },
    },
  },
  'product.measurements.mobile.live-fi': {
    key: 'product.measurements.mobile.live-fi',
    kind: 'screenshot',
    src: measurementLiveFiMedia,
    alt: 'Lehti AI:n mobiilimittaustyötila näyttää puhelinskannauksen arvot ja muutokset julkiseen metsävaratietoon verrattuna',
    aspect: 'portrait',
    focalPoint: 'top',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-09-05T00:00:00Z',
      screenshotRoute:
        '/app/measurements/properties/178-415-5-2?stand=27388643&source=campaign%3A990&compare=public&view=summary&pane=analysis',
      screenshotSeed: 'admin-property-178-415-5-2-measurement-detail-crop',
      dataDependencies: ['property:178-415-5-2', 'measurement-campaign:990', 'stand:27388643'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      mobile: {
        aspect: 'portrait',
        focalPoint: '82% 46%',
        scale: 1,
        note: 'Governed mobile detail crop prioritizes the measured-versus-public values',
      },
    },
  },
  'product.offer-review.demo': {
    key: 'product.offer-review.demo',
    kind: 'screenshot',
    src: offerReviewProductMedia,
    alt: 'Offer intake workspace showing the honest unavailable state and owner confirmation boundary',
    credit: 'Lehti AI product example · demonstration offer data · no paid result shown',
    aspect: 'panorama',
    focalPoint: 'center',
    dominantTone: 'warm',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-07-14T00:00:00Z',
      screenshotRoute: '/app/implement/90-428-1-32',
      screenshotSeed: 'landing-real-route-90-428-1-32',
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.offer-workflow.live-fi': {
    key: 'product.offer-workflow.live-fi',
    kind: 'screenshot',
    src: offerWorkflowLiveFiMedia,
    alt: 'Lehti AI:n suomenkielinen toteutustyötila näyttää tarjousten latauksen, ilmaisen esikatselun ja Tarjousten purun hinnan',
    aspect: 'wide',
    focalPoint: 'top',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-09-05T00:00:00Z',
      screenshotRoute: '/app/implement/178-415-5-2',
      screenshotSeed: 'admin-property-178-415-5-2-offer-intake',
      dataDependencies: ['property:178-415-5-2', 'offer-decode:intake'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.offer-workflow.mobile.live-fi': {
    key: 'product.offer-workflow.mobile.live-fi',
    kind: 'screenshot',
    src: offerWorkflowLiveFiMedia,
    alt: 'Lehti AI:n toteutustyötilan rajaus näyttää tarjousten latauksen ja Tarjousten purun hinnan',
    aspect: 'portrait',
    focalPoint: 'top',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-09-05T00:00:00Z',
      screenshotRoute: '/app/implement/178-415-5-2',
      screenshotSeed: 'admin-property-178-415-5-2-offer-intake-detail-crop',
      dataDependencies: ['property:178-415-5-2', 'offer-decode:intake'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      mobile: {
        aspect: 'portrait',
        focalPoint: '35% 30%',
        scale: 1,
        note: 'Governed mobile crop keeps upload, free preview, and served price visible',
      },
    },
  },
  'product.planning.demo': {
    key: 'product.planning.demo',
    kind: 'screenshot',
    src: planningDemoMedia,
    alt: 'Operations Planning decision snapshot with economics, operations, and timeline controls',
    credit: 'Lehti AI product example · demonstration property data · aerial tiles © Esri',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'warm',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-07-14T00:00:00Z',
      screenshotRoute: '/app/operations-planning/90-428-1-32',
      screenshotSeed: 'landing-real-route-90-428-1-32',
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: {
      webglAllowed: false,
    },
  },
  'product.planning.success-fi': {
    key: 'product.planning.success-fi',
    kind: 'poster',
    src: planningSuccessFiMedia,
    alt: 'Havainnollistava varakuva toimenpidesuunnitelmasta, jossa kartta, tunnusluvut ja aikajana ovat yhdessä näkymässä',
    aspect: 'wide',
    focalPoint: 'top',
    dominantTone: 'green',
    provenance: {
      source: 'skogiq-data-render',
      createdAt: '2026-09-05T00:00:00Z',
      dataDependencies: ['fallback:planning-proof'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.simulation.lens-value.live-fi': {
    key: 'product.simulation.lens-value.live-fi',
    kind: 'poster',
    src: planningSuccessFiMedia,
    alt: 'Havainnollistava varakuva koko jakson arvoa painottavasta suunnitelmasta',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'green',
    provenance: {
      source: 'skogiq-data-render',
      createdAt: '2026-09-05T00:00:00Z',
      dataDependencies: ['fallback:simulation-lens-value'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.simulation.lens-income.live-fi': {
    key: 'product.simulation.lens-income.live-fi',
    kind: 'poster',
    src: planningSuccessFiMedia,
    alt: 'Havainnollistava varakuva lähivuosien tuloja painottavasta suunnitelmasta',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'green',
    provenance: {
      source: 'skogiq-data-render',
      createdAt: '2026-09-05T00:00:00Z',
      dataDependencies: ['fallback:simulation-lens-income'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.simulation.lens-continuous.live-fi': {
    key: 'product.simulation.lens-continuous.live-fi',
    kind: 'poster',
    src: planningSuccessFiMedia,
    alt: 'Havainnollistava varakuva avohakkuuta välttävästä suunnitelmasta',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'green',
    provenance: {
      source: 'skogiq-data-render',
      createdAt: '2026-09-05T00:00:00Z',
      dataDependencies: ['fallback:simulation-lens-continuous'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.simulation.lens-guidelines.live-fi': {
    key: 'product.simulation.lens-guidelines.live-fi',
    kind: 'poster',
    src: planningSuccessFiMedia,
    alt: 'Havainnollistava varakuva Tapion suosituksia seuraavasta suunnitelmasta',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'green',
    provenance: {
      source: 'skogiq-data-render',
      createdAt: '2026-09-05T00:00:00Z',
      dataDependencies: ['fallback:simulation-lens-guidelines'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
  },
  'product.valuation.live-fi': {
    key: 'product.valuation.live-fi',
    kind: 'screenshot',
    src: valuationLiveFiMedia,
    alt: 'Lehti AI:n suomenkielinen metsätila-arvio näyttää palvellun arvovälin, laskentajakson ja puuttuvan näytön',
    aspect: 'wide',
    focalPoint: 'top',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-09-05T00:00:00Z',
      screenshotRoute: '/app/price-estimate/178-415-5-2',
      screenshotSeed: 'admin-property-178-415-5-2-served-valuation',
      dataDependencies: ['property:178-415-5-2', 'valuation:served-summary'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      split: {
        aspect: 'wide',
        focalPoint: '48% 70%',
        scale: 1.15,
        note: 'Keep the served value range, period, and action visible',
      },
    },
  },
  'product.valuation.basis-mobile.live-fi': {
    key: 'product.valuation.basis-mobile.live-fi',
    kind: 'screenshot',
    src: valuationLiveFiMedia,
    alt: 'Lehti AI:n metsätila-arvion mobiilirajaus näyttää palvellun arvovälin ja laskentajakson',
    aspect: 'portrait',
    focalPoint: 'center',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-09-05T00:00:00Z',
      screenshotRoute: '/app/price-estimate/178-415-5-2',
      screenshotSeed: 'admin-property-178-415-5-2-valuation-detail-crop',
      dataDependencies: ['property:178-415-5-2', 'valuation:served-summary'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      mobile: {
        aspect: 'portrait',
        focalPoint: '48% 72%',
        scale: 1,
        note: 'Governed mobile detail crop keeps the served range and action readable',
      },
    },
  },
  'product.planning.export': {
    key: 'product.planning.export',
    kind: 'screenshot',
    src: planningExportMedia,
    alt: 'Operations Planning export confirmation over the reviewed property plan and forest map',
    credit: 'Lehti AI product example · demonstration property data · aerial tiles © Esri',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'green',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-07-16T00:00:00Z',
      screenshotRoute: '/app/operations-planning/90-428-1-32',
      screenshotSeed: 'landing-real-route-90-428-1-32-export-confirmation',
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: {
      webglAllowed: false,
    },
  },
  'product.gallery.fallback-demo': {
    key: 'product.gallery.fallback-demo',
    kind: 'screenshot',
    src: planningDemoMedia,
    alt: 'Planning preview retained while delivered gallery media is unavailable',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'warm',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-07-14T00:00:00Z',
      screenshotRoute: '/app/debug/design-system',
      screenshotSeed: 'product-fallback-gallery',
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: {
      webglAllowed: false,
    },
  },
  'product.landing.property-hero': {
    key: 'product.landing.property-hero',
    kind: 'screenshot',
    src: propertyOverviewProductMedia,
    alt: 'Property overview with public-source summary, stand count, volume, and owner actions',
    credit: 'Lehti AI product example · demonstration property data',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-07-14T00:00:00Z',
      screenshotRoute: '/app/forest-overview/90-428-1-32',
      screenshotSeed: 'landing-real-route-90-428-1-32',
      dataDependencies: ['property:90-428-1-32', 'inventory:90-428-1-32'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      background: {
        aspect: 'wide',
        focalPoint: 'center',
        note: 'Keep mapped stands and source summary visible behind the hero story',
      },
      mobile: {
        aspect: 'portrait',
        focalPoint: '56% 50%',
        note: 'Keep the mapped property center visible on narrow screens',
      },
    },
  },
  'product.forest-hero.backdrop': {
    key: 'product.forest-hero.backdrop',
    kind: 'image',
    src: stockForestRoadPlan,
    alt: 'A quiet Nordic conifer forest opening into a moss-covered path',
    license: 'Pexels License',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    requiresCredit: false,
    provenance: {
      source: 'licensed-stock',
      createdAt: '2026-09-04T18:05:00Z',
      creator: 'Lauri Poldre',
      sourceUrl:
        'https://www.pexels.com/photo/serene-forest-road-winding-through-dense-greenery-38058750/',
      licenseUrl: 'https://www.pexels.com/license/',
      retrievedAt: '2026-09-04T18:05:00Z',
      licenseVersion: 'Pexels License accessed 2026-09-04',
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      background: {
        aspect: 'wide',
        focalPoint: 'center',
        note: 'Keep the quiet forest opening behind the copy and showcase object',
      },
      mobile: {
        aspect: 'wide',
        focalPoint: 'center',
        note: 'Keep the forest opening visible behind the compact showcase object',
      },
    },
  },
  'product.forest-hero.alternatives': {
    key: 'product.forest-hero.alternatives',
    kind: 'image',
    src: forestHeroAlternatives,
    alt: 'A layered Nordic mixed forest with spruce, pine, and rowan trees',
    license: 'Pexels License',
    aspect: 'wide',
    focalPoint: '52% 50%',
    dominantTone: 'dark',
    requiresCredit: false,
    provenance: {
      source: 'licensed-stock',
      createdAt: '2026-09-05T00:00:00Z',
      creator: 'Lauri Poldre',
      sourceUrl: 'https://www.pexels.com/photo/deep-evergreen-forest-26443498/',
      licenseUrl: 'https://www.pexels.com/license/',
      retrievedAt: '2026-09-05T00:00:00Z',
      licenseVersion: 'Pexels License accessed 2026-09-05',
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      background: {
        aspect: 'wide',
        focalPoint: '52% 50%',
        note: 'Keep the layered forest depth around the centered comparison visual',
      },
      mobile: {
        aspect: 'portrait',
        focalPoint: '54% 50%',
        note: 'Keep the central pine trunks and rowan understory in the narrow crop',
      },
    },
  },
  'product.forest-hero.income': {
    key: 'product.forest-hero.income',
    kind: 'image',
    src: forestHeroIncome,
    alt: 'A quiet green meadow at the edge of a Nordic deciduous forest',
    license: 'Pexels License',
    aspect: 'wide',
    focalPoint: '50% 48%',
    dominantTone: 'dark',
    requiresCredit: false,
    provenance: {
      source: 'licensed-stock',
      createdAt: '2026-09-05T00:00:00Z',
      creator: 'Lauri Poldre',
      sourceUrl: 'https://www.pexels.com/photo/trees-in-a-forest-17119410/',
      licenseUrl: 'https://www.pexels.com/license/',
      retrievedAt: '2026-09-05T00:00:00Z',
      licenseVersion: 'Pexels License accessed 2026-09-05',
      replacementPolicy: 'stable',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      background: {
        aspect: 'wide',
        focalPoint: '50% 48%',
        note: 'Keep the calm meadow edge around the centered financial visual',
      },
      mobile: {
        aspect: 'portrait',
        focalPoint: '52% 48%',
        note: 'Keep the young deciduous stand and meadow visible in the narrow crop',
      },
    },
  },
  'product.property-map.showcase': {
    key: 'product.property-map.showcase',
    kind: 'screenshot',
    src: propertyOverviewProductMedia,
    alt: 'Lehti AI property overview showing mapped forest stands and owner-facing summary facts',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-07-14T00:00:00Z',
      screenshotRoute: '/app/forest-overview/90-428-1-32',
      screenshotSeed: 'landing-real-route-90-428-1-32',
      dataDependencies: ['property:90-428-1-32', 'inventory:90-428-1-32'],
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      split: {
        aspect: 'wide',
        focalPoint: 'center',
        note: 'Keep the map and the owner summary legible inside the hero showcase object',
      },
      mobile: {
        aspect: 'wide',
        focalPoint: 'center',
        note: 'Preserve the complete product preview on narrow screens',
      },
    },
  },
  'product.property-map.demo': {
    key: 'product.property-map.demo',
    kind: 'screenshot',
    src: propertyOverviewProductMedia,
    alt: 'Registered property overview map with forest stand cells and summary facts',
    credit: 'Lehti AI product example · demonstration property data',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-07-14T00:00:00Z',
      screenshotRoute: '/app/forest-overview/90-428-1-32',
      screenshotSeed: 'landing-real-route-90-428-1-32',
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: { webglAllowed: false },
    cropProfiles: {
      split: {
        aspect: 'wide',
        focalPoint: 'center',
        note: 'Property map and stand cells remain legible',
      },
      mobile: {
        aspect: 'wide',
        focalPoint: 'center',
        note: 'Preserve the registered map overview on narrow screens',
      },
    },
  },
  'report.forest-overview.hero': {
    key: 'report.forest-overview.hero',
    kind: 'screenshot',
    src: forestOverviewReportMedia,
    alt: 'Forest overview report media with stand cells and summary metrics',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'light',
    provenance: {
      source: 'skogiq-app',
      createdAt: '2026-07-10T00:00:00Z',
      screenshotRoute: '/app/report/forest-overview',
      screenshotSeed: 'demo-property-001',
      replacementPolicy: 'regenerate-on-release',
    },
    webglPolicy: {
      webglAllowed: false,
    },
  },
  'map.rain-evidence.poster': {
    key: 'map.rain-evidence.poster',
    kind: 'poster',
    src: rainEvidenceMapPoster,
    alt: 'Rain evidence map poster with radar bands over stand boundaries',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    provenance: {
      source: 'skogiq-data-render',
      createdAt: '2026-07-10T00:00:00Z',
      dataDependencies: ['forecast:2026-07'],
      replacementPolicy: 'regenerate-on-data-change',
    },
    webglPolicy: {
      webglAllowed: false,
    },
  },
  'forest.editorial.trail-evening': {
    key: 'forest.editorial.trail-evening',
    kind: 'image',
    src: stockForestTrailEvening,
    alt: 'Sunlit pine forest trail in summer evening light',
    aspect: 'wide',
    focalPoint: 'center bottom',
    dominantTone: 'dark',
    license: 'Pexels License',
    requiresCredit: false,
    provenance: {
      source: 'licensed-stock',
      createdAt: '2026-07-11T20:35:00Z',
      creator: 'Lauri Poldre',
      sourceUrl:
        'https://www.pexels.com/photo/tranquil-forest-trail-in-summer-evening-light-35576600/',
      licenseUrl: 'https://www.pexels.com/license/',
      // Retrieval date = this record's createdAt; licence terms owner-confirmed
      // 2026-10-02 (Pexels License: free use, attribution not required).
      retrievedAt: '2026-07-11T20:35:00Z',
      licenseVersion: 'Pexels License; attribution not required (owner-confirmed 2026-10-02)',
      replacementPolicy: 'stable',
    },
    webglPolicy: {
      webglAllowed: false,
    },
  },
  'forest.editorial.pine-understory': {
    key: 'forest.editorial.pine-understory',
    kind: 'image',
    src: stockForestPineUnderstory,
    alt: 'Small pine sapling growing in a mossy forest understory',
    aspect: 'wide',
    focalPoint: '42% 58%',
    dominantTone: 'dark',
    license: 'Pexels License',
    requiresCredit: false,
    provenance: {
      source: 'licensed-stock',
      createdAt: '2026-07-11T20:35:00Z',
      creator: 'Lauri Poldre',
      sourceUrl:
        'https://www.pexels.com/photo/close-up-of-small-pine-tree-in-forest-understory-33435491/',
      licenseUrl: 'https://www.pexels.com/license/',
      // Retrieval date = this record's createdAt; licence terms owner-confirmed
      // 2026-10-02 (Pexels License: free use, attribution not required).
      retrievedAt: '2026-07-11T20:35:00Z',
      licenseVersion: 'Pexels License; attribution not required (owner-confirmed 2026-10-02)',
      replacementPolicy: 'stable',
    },
    webglPolicy: {
      webglAllowed: false,
    },
  },
  'forest.editorial.road-plan': {
    key: 'forest.editorial.road-plan',
    kind: 'image',
    src: stockForestRoadPlan,
    alt: 'A quiet forest road curving through dense green trees',
    aspect: 'wide',
    focalPoint: 'center',
    dominantTone: 'dark',
    license: 'Pexels License',
    requiresCredit: false,
    provenance: {
      source: 'licensed-stock',
      createdAt: '2026-07-11T20:35:00Z',
      creator: 'Lauri Poldre',
      sourceUrl:
        'https://www.pexels.com/photo/serene-forest-road-winding-through-dense-greenery-38058750/',
      licenseUrl: 'https://www.pexels.com/license/',
      // Retrieval date = this record's createdAt; licence terms owner-confirmed
      // 2026-10-02 (Pexels License: free use, attribution not required).
      retrievedAt: '2026-07-11T20:35:00Z',
      licenseVersion: 'Pexels License; attribution not required (owner-confirmed 2026-10-02)',
      replacementPolicy: 'stable',
    },
    webglPolicy: {
      webglAllowed: false,
    },
  },
  'forest.editorial.boardwalk': {
    key: 'forest.editorial.boardwalk',
    kind: 'image',
    src: stockForestBoardwalk,
    alt: 'Wooden boardwalk leading through a green wetland forest',
    aspect: 'wide',
    focalPoint: 'center bottom',
    dominantTone: 'green',
    license: 'Pexels License',
    requiresCredit: false,
    provenance: {
      source: 'licensed-stock',
      createdAt: '2026-07-11T20:35:00Z',
      creator: 'Lauri Poldre',
      sourceUrl:
        'https://www.pexels.com/photo/scenic-wooden-boardwalk-through-lush-green-forest-36756725/',
      licenseUrl: 'https://www.pexels.com/license/',
      // Retrieval date = this record's createdAt; licence terms owner-confirmed
      // 2026-10-02 (Pexels License: free use, attribution not required).
      retrievedAt: '2026-07-11T20:35:00Z',
      licenseVersion: 'Pexels License; attribution not required (owner-confirmed 2026-10-02)',
      replacementPolicy: 'stable',
    },
    webglPolicy: {
      webglAllowed: false,
    },
  },
} satisfies Record<string, FmMediaAsset>;

/** Closed identity set for product-story heroes; raw strings/URLs never type-check. */
export type FmRegisteredProductMediaIdentity = Extract<
  keyof typeof CORE_MEDIA_REGISTRY,
  `product.${string}`
>;

export const LOCAL_MEDIA_REGISTRY: Record<string, FmMediaAsset> = {
  ...CORE_MEDIA_REGISTRY,
  ...CONTEXT_MEDIA_REGISTRY,
  ...PUBLISHED_MEDIA_REGISTRY,
};

/** Static catalog export for generation/audit tooling. Runtime lookup may override delivery. */
export const MEDIA_REGISTRY = LOCAL_MEDIA_REGISTRY;

/** Temporary migration aliases. New descriptors must use semantic identities. */
export const LEGACY_MEDIA_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  'stock.forest.trail-evening': 'forest.editorial.trail-evening',
  'stock.forest.pine-understory': 'forest.editorial.pine-understory',
  'stock.forest.road-plan': 'forest.editorial.road-plan',
  'stock.forest.boardwalk': 'forest.editorial.boardwalk',
});

const mediaDeliveryListeners = new Set<() => void>();
let deliveredMedia: Readonly<Record<string, FmMediaAsset>> = {};
let mediaDeliveryVersion = 'local-fallback';
let mediaDeliveryRevision = 0;
const scopedDeliveredMedia = new Map<symbol, Readonly<Record<string, FmMediaAsset>>>();

function notifyMediaDeliveryChanged(): void {
  mediaDeliveryRevision += 1;
  mediaDeliveryListeners.forEach((listener) => listener());
}

export function installDeliveredMedia(
  version: string,
  assets: Readonly<Record<string, FmMediaAsset>>,
): void {
  deliveredMedia = Object.freeze({ ...assets });
  mediaDeliveryVersion = version;
  notifyMediaDeliveryChanged();
}

/**
 * Installs a gallery/test-only delivery overlay without replacing the live
 * manifest. The returned cleanup removes only this lease, so sibling stories
 * and manifest refreshes retain their exact assets and catalog version.
 */
export function installScopedDeliveredMediaOverride(
  assets: Readonly<Record<string, FmMediaAsset>>,
): () => void {
  const lease = Symbol('scoped-media-delivery');
  scopedDeliveredMedia.set(lease, Object.freeze({ ...assets }));
  notifyMediaDeliveryChanged();
  return () => {
    if (!scopedDeliveredMedia.delete(lease)) return;
    notifyMediaDeliveryChanged();
  };
}

export function subscribeMediaDelivery(listener: () => void): () => void {
  mediaDeliveryListeners.add(listener);
  return () => mediaDeliveryListeners.delete(listener);
}

export function getMediaDeliveryVersion(): string {
  return mediaDeliveryVersion;
}

export function getMediaDeliveryRevision(): number {
  return mediaDeliveryRevision;
}

/**
 * Lookup a media asset by key. Throws if not found (fail-fast for dev).
 */
export function getMediaAsset(key: string): FmMediaAsset {
  const identity = LEGACY_MEDIA_ALIASES[key] ?? key;
  const scopedAsset = [...scopedDeliveredMedia.values()]
    .reverse()
    .find((assets) => assets[identity])?.[identity];
  const asset = scopedAsset ?? deliveredMedia[identity] ?? LOCAL_MEDIA_REGISTRY[identity];
  if (!asset) {
    throw new Error(
      `Media asset not found: "${key}". Register it in mediaRegistry.ts. ` +
        `Available keys: ${Object.keys(MEDIA_REGISTRY).join(', ') || '(none yet)'}`,
    );
  }
  return asset;
}

export function getLocalMediaAsset(key: string): FmMediaAsset {
  const identity = LEGACY_MEDIA_ALIASES[key] ?? key;
  const asset = LOCAL_MEDIA_REGISTRY[identity];
  if (!asset) throw new Error(`Local media fallback not found: "${key}"`);
  return asset;
}

/**
 * Asset availability helper: checks if an asset is registered.
 */
export function hasMediaAsset(key: string): boolean {
  const identity = LEGACY_MEDIA_ALIASES[key] ?? key;
  return identity in LOCAL_MEDIA_REGISTRY;
}

/**
 * Get all registered media keys. Useful for validation and auditing.
 */
export function getAllMediaKeys(): string[] {
  return Object.keys(LOCAL_MEDIA_REGISTRY);
}
